/**
 * Skylit Studio Customer Hub
 * Slide-out drawer showing customer orders with ebook links and tracking.
 */

class SkylitCustomerHub {
  constructor() {
    this.container = document.getElementById('skylit-hub');
    if (!this.container) return;

    this.drawer = this.container.querySelector('.skylit-hub__drawer');
    this.trigger = document.getElementById('skylit-hub-trigger');
    this.closeBtn = this.container.querySelector('.skylit-hub__close');
    this.backdrop = this.container.querySelector('.skylit-hub__backdrop');
    this.ordersContainer = document.getElementById('skylit-hub-orders');
    this.accountContainer = document.getElementById('skylit-hub-account');
    this.offersContainer = document.getElementById('skylit-hub-offers');
    this.contactContainer = document.getElementById('skylit-hub-contact');
    this.navButtons = this.container.querySelectorAll('.skylit-hub__nav-btn');

    this.isLoggedIn = this.container.dataset.customerLoggedIn === 'true';
    this.customerEmail = this.container.dataset.customerEmail || '';
    this.customerPhone = this.container.dataset.customerPhone || '';
    this.customerName = this.container.dataset.customerName || '';
    this.customerFirstName = this.container.dataset.customerFirstName || '';
    this.customerLastName = this.container.dataset.customerLastName || '';
    this.customerId = this.container.dataset.customerId || '';
    this.apiUrl = this.container.dataset.apiUrl || 'https://admin.skylitstudio.co.uk/api/customer-orders/';
    // Session minting goes through the Shopify App Proxy so the backend can
    // verify WHO is logged in (signed logged_in_customer_id) instead of
    // trusting the email we send it. Same-origin, so no CORS involved.
    this.sessionUrl = this.container.dataset.sessionUrl || '/apps/skylit/create-session';
    this.accountUrl = this.container.dataset.accountUrl || '/account';
    this.addressesUrl = this.container.dataset.addressesUrl || '/account/addresses';
    this.logoutUrl = this.container.dataset.logoutUrl || '/account/logout';

    // Session token for secure authentication
    this.sessionToken = null;
    this.sessionTokenExpiry = null;

    this.ordersLoaded = false;
    this.offersLoaded = false;
    this.ordersData = null;
    this.shopifyOrders = null;
    this.djangoOrders = null;
    this.selectedOrder = null;
    this.currentTab = 'orders';
    this.customerAddresses = this.loadAddresses();
    this.shopifyOrders = this.loadShopifyOrders();

    this.init();
  }

  init() {
    // Event listeners
    this.trigger?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());

    // Tab navigation
    this.navButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.tab;
        if (tab && !btn.disabled) {
          this.switchTab(tab);
        }
      });
    });

    // Handle URL hash for direct link (e.g., #s-hub, #my-orders)
    this.checkHash();
    window.addEventListener('hashchange', () => this.checkHash());

    // Handle bfcache (back-forward cache) restoration
    // When page is restored from cache, verify drawer state matches URL
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        const hash = window.location.hash;
        const isSimpleHash = hash === '#my-orders' || hash === '#customer-hub' || hash === '#s-hub';
        const isTabHash = hash.match(/^#s-hub\/(.+)$/);
        const shouldBeOpen = isSimpleHash || isTabHash;

        if (this.isOpen() && !shouldBeOpen) {
          this.close();
        } else if (!this.isOpen() && shouldBeOpen) {
          this.open();
        }
      }
    });

    // Keyboard accessibility
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Listen for custom event to open drawer
    window.addEventListener('skylit-hub:open', () => this.open());
  }

  loadAddresses() {
    try {
      const script = document.getElementById('skylit-hub-addresses');
      if (script) {
        return JSON.parse(script.textContent);
      }
    } catch (e) {
      console.error('[CustomerHub] Failed to parse addresses:', e);
    }
    return [];
  }

  loadShopifyOrders() {
    try {
      const script = document.getElementById('skylit-hub-shopify-orders');
      if (script) {
        const orders = JSON.parse(script.textContent);
        // Debug: console.log('[CustomerHub] Loaded Shopify orders:', orders.length);
        return orders;
      }
    } catch (e) {
      console.error('[CustomerHub] Failed to parse Shopify orders:', e);
    }
    return [];
  }

  switchTab(tab) {
    if (tab === this.currentTab) return;

    this.currentTab = tab;

    // Update nav button states
    this.navButtons.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.tab === tab);
    });

    // Show/hide content
    if (tab === 'orders') {
      this.ordersContainer.style.display = '';
      this.accountContainer.style.display = 'none';
      this.offersContainer.style.display = 'none';
      this.contactContainer.style.display = 'none';
    } else if (tab === 'account') {
      this.ordersContainer.style.display = 'none';
      this.accountContainer.style.display = '';
      this.offersContainer.style.display = 'none';
      this.contactContainer.style.display = 'none';
      this.renderAccount();
    } else if (tab === 'offers') {
      this.ordersContainer.style.display = 'none';
      this.accountContainer.style.display = 'none';
      this.offersContainer.style.display = '';
      this.contactContainer.style.display = 'none';
      this.renderOffers();
    } else if (tab === 'contact') {
      this.ordersContainer.style.display = 'none';
      this.accountContainer.style.display = 'none';
      this.offersContainer.style.display = 'none';
      this.contactContainer.style.display = '';
      this.renderContact();
    }
  }

  checkHash() {
    const hash = window.location.hash;

    // Check for simple hashes: #s-hub, #my-orders, #customer-hub
    const isSimpleHash = hash === '#my-orders' || hash === '#customer-hub' || hash === '#s-hub';

    // Check for tab-specific hashes: #s-hub/chat, #s-hub/orders, #s-hub/account
    const tabMatch = hash.match(/^#s-hub\/(.+)$/);
    const tabName = tabMatch ? tabMatch[1] : null;

    if (isSimpleHash || tabMatch) {
      // Verify hash still exists when timeout fires (prevents stale opens)
      setTimeout(() => {
        const currentHash = window.location.hash;
        const currentIsSimple = currentHash === '#my-orders' || currentHash === '#customer-hub' || currentHash === '#s-hub';
        const currentTabMatch = currentHash.match(/^#s-hub\/(.+)$/);

        if (currentIsSimple || currentTabMatch) {
          // If a specific tab was requested, switch to it BEFORE opening
          if (currentTabMatch) {
            let requestedTab = currentTabMatch[1];

            // Map URL names to internal tab names
            const tabMapping = {
              'chat': 'contact',
              'contact': 'contact',
              'orders': 'orders',
              'offers': 'offers',
              'account': 'account'
            };

            const mappedTab = tabMapping[requestedTab];

            // Validate and switch to tab
            if (mappedTab && ['orders', 'contact', 'account', 'offers'].includes(mappedTab)) {
              this.switchTab(mappedTab);
            }
          }

          // Then open the drawer
          this.open();
        }
      }, 100);
    }
  }

  isOpen() {
    return this.drawer?.classList.contains('is-open');
  }

  open() {
    if (!this.drawer) return;

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.backdrop?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    // Focus the close button
    this.closeBtn?.focus();

    // Load orders if logged in and not already loaded
    if (this.isLoggedIn && !this.ordersLoaded) {
      this.loadOrders();
    }
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.backdrop?.classList.remove('is-visible');
    document.body.style.overflow = '';

    // Clear hash if it was used to open (including tab-specific hashes)
    if (window.location.hash.match(/^#(my-orders|customer-hub|s-hub)/)) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  async loadOrders() {
    if (!this.ordersContainer || !this.customerEmail) {
      this.renderError('Unable to load orders. Please try again later.');
      return;
    }

    try {
      // Load Django map data (custom configurations, ebooks)
      const djangoOrders = await this.loadDjangoOrders();
      this.djangoOrders = djangoOrders;

      // Merge Shopify orders with Django data
      const mergedOrders = this.mergeOrders(this.shopifyOrders, djangoOrders);

      this.ordersData = { orders: mergedOrders };
      this.ordersLoaded = true;

      // Debug: console.log('[CustomerHub] Merged orders:', mergedOrders.length);
      this.renderOrders(mergedOrders);

    } catch (error) {
      console.error('[CustomerHub] Failed to load orders:', error);
      this.renderError('Unable to load orders. Please try again.');
    }
  }

  async loadDjangoOrders() {
    try {
      // Ensure we have a valid session token
      const token = await this.ensureSessionToken();

      // Build API URL with token
      const url = new URL(this.apiUrl);
      url.searchParams.set('token', token);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      // Debug: console.log('[CustomerHub] Loaded Django orders:', data.orders?.length || 0);
      return data.orders || [];

    } catch (error) {
      console.error('[CustomerHub] Failed to load Django orders:', error);
      return [];
    }
  }

  // A backend design entry's own human label ("Star Map — London, 12 June
  // 2026"), mirrored client-side from app/api/customer-orders/route.ts's
  // labelForDesign. Needed here because gift notes are now gathered from
  // MULTIPLE design entries into one merged card (see collectGiftNotes) —
  // each entry's own note usually arrives unlabelled from the API (that
  // entry had exactly one note, so the server had no reason to label it),
  // so the client has to attach the label once notes from several designs
  // land together in the same card.
  buildGiftNoteDesignLabel(dOrder) {
    const productLabel = dOrder?.product_label || '';
    const location = dOrder?.location || '';
    const dateText = dOrder?.date_text || '';
    const title = dOrder?.title || '';
    const detail = [location, dateText].filter(Boolean).join(', ');
    if (productLabel) return detail ? `${productLabel} — ${detail}` : productLabel;
    if (detail) return detail;
    if (title) return title;
    return null;
  }

  // Gathers gift notes across EVERY backend design entry that shares one
  // Shopify order — not just the single "winning" entry the rest of the
  // card's fields come from. customer-orders returns one entry per design
  // (a bundle of 4 prints in one Shopify order is 4 entries), each correctly
  // carrying only its OWN note; before this, only the winning entry's note
  // ever reached the merged card, so a customer with 4 notes saw 1 and
  // couldn't reach the other 3.
  //
  // Deduped by note id (a null-paired_order_id note legitimately sits on
  // exactly one design entry server-side, but this stays defensive so a
  // note can never render twice), order preserved (designEntries is in the
  // API's stable push order, so the list doesn't shuffle between loads).
  collectGiftNotes(designEntries, giftData, customData) {
    const seen = new Set();
    const gathered = [];
    designEntries.forEach(dOrder => {
      const notes = Array.isArray(dOrder.gift_notes) ? dOrder.gift_notes : [];
      const designLabel = this.buildGiftNoteDesignLabel(dOrder);
      notes.forEach(n => {
        if (n.id != null) {
          if (seen.has(n.id)) return;
          seen.add(n.id);
        }
        gathered.push({ ...n, _designLabel: designLabel });
      });
    });

    if (gathered.length === 0) {
      // No backend note anywhere for this order — fall back to the cart
      // property/note-attribute text, same as the pre-array behaviour.
      // No known print state (editable: null), so it renders read-only
      // with no "already printed" claim we can't back up.
      const legacyGiftNote = giftData.gift_note || customData.gift_note || '';
      return legacyGiftNote
        ? [{ id: null, note: legacyGiftNote, note_original: null, editable: null, label: null }]
        : [];
    }

    const total = gathered.length;
    return gathered.map((n, i) => {
      let label = null;
      if (total > 1) {
        // Trust a label the server already computed (it labels a note when
        // THAT note's own design entry ended up with more than one note —
        // e.g. a null-paired legacy note bundled alongside a directly-paired
        // one). Otherwise this is the common case the server had no reason
        // to label (each entry it saw had exactly one note): use the design
        // the note came from, or a positional fallback if that design can't
        // be named.
        label = n.label || n._designLabel || `Note ${i + 1} of ${total}`;
      }
      const { _designLabel, ...rest } = n;
      return { ...rest, label };
    });
  }

  mergeOrders(shopifyOrders, djangoOrders) {
    const merged = [];
    const djangoMap = new Map();
    const matched = new Set();

    // Index backend orders under every id they might be found by. Recent rows
    // store the numeric Shopify order id (13494617047417); legacy rows store the
    // short order number (1570). Indexing both is what keeps the backend record
    // (which carries any post-purchase correction) attached to the Shopify order.
    djangoOrders.forEach(dOrder => {
      [dOrder.shopify_order_id, dOrder.order_number].forEach(candidate => {
        const key = this.extractOrderNumber(candidate);
        if (key && /^\d{3,}$/.test(key) && !djangoMap.has(key)) {
          djangoMap.set(key, dOrder);
        }
      });
    });

    // Group EVERY backend design entry by shopify_order_id (not just the
    // single field-set "winner" above) purely so gift notes can be gathered
    // from all of them — every other card field still comes from the one
    // winning djangoData entry below, unchanged.
    const designEntriesBySid = new Map();
    djangoOrders.forEach(dOrder => {
      const sid = String(dOrder.shopify_order_id || '');
      if (!sid) return;
      const list = designEntriesBySid.get(sid) || [];
      list.push(dOrder);
      designEntriesBySid.set(sid, list);
    });

    // Merge Shopify orders with Django data
    shopifyOrders.forEach(sOrder => {
      let djangoData;
      for (const candidate of [sOrder.id, sOrder.order_number, sOrder.name]) {
        const key = this.extractOrderNumber(candidate);
        if (key && djangoMap.has(key)) {
          djangoData = djangoMap.get(key);
          break;
        }
      }

      // Extract custom map data from line item properties
      const customData = this.extractCustomDataFromLineItems(sOrder.line_items);

      // Extract gift wrap data from order note_attributes (cart attributes)
      const giftData = this.extractGiftDataFromNoteAttributes(sOrder.note_attributes);

      // Gift notes: gathered from every backend design entry sharing this
      // Shopify order (see collectGiftNotes) — an order can carry SEVERAL
      // notes (one per paired print, possibly across several different
      // designs), each printed independently, so this is always an array.
      //
      // Deliberately does NOT mark the sibling entries as `matched` (that
      // was tried and reverted): which entries render as their own
      // "Django-only" cards is a product/card-list decision, not something
      // a gift-note fix should change as a side effect. The card list stays
      // exactly as it was — a bundled order still produces one card per
      // design. Double-rendering is instead prevented at the note level:
      // sibling cards are stripped of gift_notes below (search
      // "no Shopify price/address/line-items"), since every note for this
      // Shopify order is already shown on this merged card.
      const designEntries = designEntriesBySid.get(String(sOrder.id)) || [];
      const mergedGiftNotes = this.collectGiftNotes(designEntries, giftData, customData);

      const mergedOrder = {
        // Shopify data (primary source)
        shopify_order_id: String(sOrder.id),
        order_number: String(sOrder.order_number),
        order_id: String(sOrder.id),
        name: sOrder.name,
        created_at: sOrder.created_at,
        updated_at: sOrder.created_at,
        financial_status: sOrder.financial_status,
        fulfillment_status: sOrder.fulfillment_status,
        cancelled: sOrder.cancelled,

        // Pricing from Shopify
        total_price: sOrder.total_price,
        subtotal_price: sOrder.subtotal_price,
        shipping_price: sOrder.total_shipping_price,
        tax_price: sOrder.total_tax,
        currency: sOrder.currency,

        // Discounts from Shopify
        discount_applications: sOrder.discount_applications || [],
        total_discounts: sOrder.total_discounts || 0,

        // Line items
        line_items: sOrder.line_items,

        // Addresses from Shopify
        shipping_address: sOrder.shipping_address,
        billing_address: sOrder.billing_address,

        // Fulfillment & Tracking from Shopify
        fulfillments: sOrder.fulfillments || [],

        // Django custom map data (if available)
        product_type: djangoData?.product_type || this.inferProductType(sOrder.line_items),
        product_label: djangoData?.product_label || this.getProductLabel(sOrder.line_items),
        location: djangoData?.location || customData.location || '',
        date_text: djangoData?.date_text || customData.date || '',
        time_text: djangoData?.time_text || customData.time || '',
        time_shown: djangoData?.time_shown !== undefined ? djangoData.time_shown : customData.time_shown,
        title: djangoData?.title || customData.title || '',
        message: djangoData?.message || customData.message || '',
        // Prioritize Shopify variant_title for proper size formatting (not "Marimea")
        size: this.extractSizeFromVariant(sOrder.line_items) || djangoData?.size || customData.size || '',
        caption: djangoData?.caption || customData.caption || '',
        ribbon_colour: giftData.ribbon_colour || customData.ribbon_colour || '',
        gift_wrap_theme: giftData.gift_wrap_theme || customData.gift_wrap_theme || '',
        gift_notes: mergedGiftNotes,
        ebook_url: djangoData?.ebook_url || null,
        has_ebook: djangoData?.has_ebook || false,
        thumbnail_url: djangoData?.thumbnail_url || this.getLineItemImage(sOrder.line_items),
      };

      // Mark as consumed so it isn't re-added as a backend-only order below
      if (djangoData) {
        matched.add(djangoData);
      }

      merged.push(mergedOrder);
    });

    // Add any Django-only orders (not in Shopify response - old orders).
    // The card list itself is unchanged: a bundled Shopify order still
    // produces one of these per sibling design, exactly as before gift
    // notes existed. Only gift_notes is touched — and only for a sibling of
    // an order that DID get a real, merged Shopify-order card above (see
    // mergedCardSids): that merged card already gathered and is showing
    // every note for this Shopify order (collectGiftNotes), so repeating
    // any of them here would render the same note twice. A genuinely
    // orphaned entry (no real Shopify order at all — old pre-sync data)
    // has no merged card covering it, so it keeps its own notes unchanged.
    const mergedCardSids = new Set(shopifyOrders.map(sOrder => String(sOrder.id)));
    djangoOrders.filter(dOrder => !matched.has(dOrder)).forEach(dOrder => {
      const sid = String(dOrder.shopify_order_id || '');
      const isSiblingOfAMergedCard = sid !== '' && mergedCardSids.has(sid);
      merged.push({
        shopify_order_id: dOrder.shopify_order_id || '',
        order_number: dOrder.order_number || dOrder.order_id,
        order_id: dOrder.order_id,
        created_at: dOrder.created_at,
        updated_at: dOrder.updated_at || dOrder.created_at,
        product_type: dOrder.product_type,
        product_label: dOrder.product_label,
        location: dOrder.location || '',
        date_text: dOrder.date_text || '',
        title: dOrder.title || '',
        message: dOrder.message || '',
        size: dOrder.size || '',
        caption: dOrder.caption || '',
        gift_notes: isSiblingOfAMergedCard
          ? []
          : (Array.isArray(dOrder.gift_notes) ? dOrder.gift_notes : []),
        ebook_url: dOrder.ebook_url,
        has_ebook: dOrder.has_ebook,
        thumbnail_url: dOrder.thumbnail_url || '',
        // No Shopify data available
        total_price: null,
        shipping_address: null,
        line_items: [],
      });
    });

    // Sort by creation date descending
    merged.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });

    return merged;
  }

  extractOrderNumber(str) {
    if (!str) return null;
    // Extract numeric order number from strings like "SkylitStudio#12345" or "#12345"
    const match = String(str).match(/\d+/);
    return match ? match[0] : String(str);
  }

  extractCustomDataFromLineItems(lineItems) {
    const data = { location: '', date: '', time: '', time_shown: null, title: '', message: '', size: '', caption: '', ribbon_colour: '', gift_wrap_theme: '', gift_note: '' };

    lineItems.forEach(item => {
      item.properties?.forEach(prop => {
        const name = prop.name?.toLowerCase() || '';
        if (name.includes('location') || name.includes('place')) {
          data.location = prop.value || '';
        } else if (name.includes('date')) {
          data.date = prop.value || '';
        } else if (name.includes('time') && !name.includes('shown') && !name.includes('hidden')) {
          data.time = prop.value || '';
        } else if (name.includes('time shown') || name.includes('time_shown') || name.includes('show time')) {
          // Convert to boolean - handles "true", "yes", "1", true, 1
          const val = String(prop.value).toLowerCase();
          data.time_shown = val === 'true' || val === 'yes' || val === '1';
        } else if (name.includes('title') || name.includes('headline')) {
          data.title = prop.value || '';
        } else if (name.includes('message') || name.includes('text')) {
          data.message = prop.value || '';
        } else if (name.includes('size')) {
          data.size = prop.value || '';
        } else if (name.includes('caption')) {
          data.caption = prop.value || '';
        } else if (name.includes('ribbon') && name.includes('colour')) {
          data.ribbon_colour = prop.value || '';
        } else if (name.includes('gift') && name.includes('wrap') && name.includes('theme')) {
          data.gift_wrap_theme = prop.value || '';
        } else if (name.includes('gift') && name.includes('note')) {
          data.gift_note = prop.value || '';
        }
      });

      var vt = String(item.variant_title || '').toLowerCase();
      if (!data.gift_wrap_theme && (vt.indexOf('gift wrap') !== -1 || String(item.title || '').toLowerCase().indexOf('gift wrap') !== -1)) {
        data.gift_wrap_theme = vt.indexOf('plaid') !== -1 || vt.indexOf('spruce') !== -1 ? 'Spruce' : vt.indexOf('botanical') !== -1 || vt.indexOf('holly') !== -1 ? 'Holly' : vt.indexOf('dot') !== -1 ? 'Gold Dots' : vt.indexOf('stripe') !== -1 ? 'Gold Stripes' : vt.indexOf('love') !== -1 ? 'Love Hearts' : 'Regular';
      }
    });

    return data;
  }

  extractGiftDataFromNoteAttributes(noteAttributes) {
    /**
     * Extract gift wrap data from order note_attributes (cart attributes).
     * These are stored at the order level, not line item level.
     */
    const data = { gift_note: '', ribbon_colour: '', gift_wrap_theme: '', gift_wrap_enabled: false };

    if (!noteAttributes || !Array.isArray(noteAttributes)) {
      return data;
    }

    noteAttributes.forEach(attr => {
      const name = attr.name?.toLowerCase() || '';
      const value = attr.value || '';

      if (name.includes('gift') && name.includes('note')) {
        data.gift_note = value;
      } else if (name.includes('ribbon') && name.includes('colo')) {
        data.ribbon_colour = value;
      } else if (name.includes('gift') && name.includes('wrap') && name.includes('theme')) {
        data.gift_wrap_theme = value;
      } else if (name.includes('gift') && name.includes('wrap') && name.includes('enabled')) {
        data.gift_wrap_enabled = value === 'true' || value === true;
      }
    });

    return data;
  }

  inferProductType(lineItems) {
    const firstItem = lineItems[0];
    if (!firstItem) return 'star_map';

    const title = firstItem.title.toLowerCase();
    if (title.includes('moon') && title.includes('family')) return 'hor_moon_map';
    if (title.includes('moon')) return 'moon_map';
    return 'star_map';
  }

  getProductLabel(lineItems) {
    const firstItem = lineItems[0];
    return firstItem?.title || 'Star Map';
  }

  extractSizeFromVariant(lineItems) {
    const firstItem = lineItems[0];

    // If variant_title exists, use it
    if (firstItem?.variant_title) {
      return firstItem.variant_title;
    }

    // Otherwise, parse from product title (e.g., "Product Name - Framed / A4 / Black")
    if (firstItem?.title) {
      const title = firstItem.title;

      // Match patterns like "A4", "A3", "50x70cm", etc.
      const sizeMatch = title.match(/\b(A[0-5]|[0-9]+x[0-9]+\s?cm)\b/i);
      if (sizeMatch) {
        return sizeMatch[1];
      }

      // If no size found, try to extract from " / Size / " pattern
      const parts = title.split('/').map(p => p.trim());
      if (parts.length >= 2) {
        // Usually format is "Framed / A4 / Black" - second part is size
        return parts[1] || '';
      }
    }

    return '';
  }

  getLineItemImage(lineItems) {
    const firstItem = lineItems[0];
    return firstItem?.image || '';
  }

  async fetchSessionToken() {
    /**
     * Fetch a secure session token via the Shopify App Proxy.
     * Identity comes from Shopify's signed logged_in_customer_id, not from
     * anything we send. Called once when the hub is opened; token cached.
     */
    try {
      const response = await fetch(this.sessionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: '{}'
      });

      if (!response.ok) {
        throw new Error('Failed to create session');
      }

      const data = await response.json();
      this.sessionToken = data.token;
      this.sessionTokenExpiry = new Date(data.expires_at);

      return this.sessionToken;
    } catch (error) {
      console.error('[CustomerHub] Session token fetch failed:', error);
      throw error;
    }
  }

  async ensureSessionToken() {
    /**
     * Ensure we have a valid session token, fetching a new one if needed.
     */
    // If no token or token expired, fetch new one
    if (!this.sessionToken || (this.sessionTokenExpiry && new Date() >= this.sessionTokenExpiry)) {
      await this.fetchSessionToken();
    }
    return this.sessionToken;
  }

  renderOrders(orders) {
    if (!this.ordersContainer) return;

    if (!orders || orders.length === 0) {
      this.renderEmpty();
      return;
    }

    // Get most recent order for the "current" card
    const currentOrder = orders[0];
    const pastOrders = orders.slice(1);

    let html = '';

    // Current/Latest Order Card
    html += this.renderCurrentOrderCard(currentOrder);

    // Past Orders Section
    if (pastOrders.length > 0) {
      html += '<div class="skylit-hub__section-header">Past orders</div>';
      html += '<ul class="skylit-hub__list">';
      html += pastOrders.map(order => this.renderListItem(order)).join('');
      html += '</ul>';
    }

    this.ordersContainer.innerHTML = html;

    // Add click handler for current order card
    const currentCard = this.ordersContainer.querySelector('.skylit-hub__current-card');
    if (currentCard) {
      currentCard.addEventListener('click', async () => {
        const orderId = currentCard.dataset.orderId;
        const order = orders.find(o => o.order_id === orderId);
        if (order) {
          await this.showOrderDetail(order);
        }
      });
    }

    // Add click handlers for list items
    this.ordersContainer.querySelectorAll('.skylit-hub__list-item').forEach(item => {
      item.addEventListener('click', async () => {
        const orderId = item.dataset.orderId;
        const order = orders.find(o => o.order_id === orderId);
        if (order) {
          await this.showOrderDetail(order);
        }
      });
    });
  }

  getOrderStatus(order) {
    // Debug logging to console
    const hash = window.location.hash;
    const isHubHash = hash === '#s-hub' || hash === '#customer-hub' || hash.startsWith('#s-hub/');
    // Debug: Order status logging removed to reduce console output
    // if (isHubHash) {
    //   console.log('Order status debug:', {
    //     order_id: order.order_id,
    //     cancelled: order.cancelled,
    //     financial_status: order.financial_status,
    //     fulfillment_status: order.fulfillment_status,
    //     has_ebook: order.has_ebook,
    //     fulfillments: order.fulfillments,
    //     tracking_number: order.fulfillments?.[0]?.tracking_number
    //   });
    // }

    // Priority 1: Check if cancelled
    if (order.cancelled === true || order.cancelled === 'true') {
      return {
        text: 'Cancelled',
        progress: 0,
        isComplete: false
      };
    }

    // Priority 2: Check financial status for payment issues
    if (order.financial_status === 'refunded' || order.financial_status === 'partially_refunded') {
      return {
        text: 'Refunded',
        progress: 0,
        isComplete: false
      };
    }

    if (order.financial_status === 'pending' || order.financial_status === 'voided') {
      return {
        text: 'Payment pending',
        progress: 1,
        isComplete: false
      };
    }

    // Check if there's a tracking number (order shipped but not necessarily delivered)
    const hasTracking = order.fulfillments &&
                       order.fulfillments.length > 0 &&
                       order.fulfillments[0].tracking_number &&
                       order.fulfillments[0].tracking_number.trim() !== '';

    const hasFulfillmentData = order.fulfillments && order.fulfillments.length > 0;

    // Check if actually delivered (shipment_status from Shopify fulfillment API)
    const isDelivered = hasFulfillmentData &&
                       order.fulfillments.some(f => f.shipment_status?.toLowerCase() === 'delivered');

    // Priority 3: Check fulfillment status
    // Note: Shopify fulfillment_status can be: null, "fulfilled", "partial", or "restocked"
    // IMPORTANT: fulfillment_status can be "fulfilled" even when fulfillments array is empty
    // This happens when Shopify marks order as fulfilled but customer.orders doesn't include full fulfillment details

    if (order.fulfillment_status === 'fulfilled' || order.fulfillment_status === 'shipped') {
      // If we have actual fulfillment data, check if delivered
      if (hasFulfillmentData) {
        // Check if delivered
        if (isDelivered) {
          return {
            text: 'Delivered',
            progress: 4,
            isComplete: true
          };
        }

        // Otherwise it's shipped
        return {
          text: 'Shipped',
          progress: 3,
          isComplete: false
        };
      }

      // If marked as fulfilled but no fulfillment details available, show as processing
      // (Shopify limitation: customer.orders doesn't always include fulfillment data)
      return {
        text: 'Preparing shipment',
        progress: 2,
        isComplete: false
      };
    }

    if (order.fulfillment_status === 'partial') {
      return {
        text: 'Partially shipped',
        progress: 2,
        isComplete: false
      };
    }

    // If has tracking but not fulfilled, it's in transit
    if (hasTracking) {
      return {
        text: 'Shipped',
        progress: 2,
        isComplete: false
      };
    }

    // Priority 4: Check if order is being processed (paid but no tracking yet)
    if (order.financial_status === 'paid' || order.financial_status === 'authorized') {
      return {
        text: 'Processing',
        progress: 2,
        isComplete: false
      };
    }

    // Default: Order placed (financial_status might be empty, "pending", etc.)
    return {
      text: 'Order placed',
      progress: 1,
      isComplete: false
    };
  }

  renderStackedThumbnails(order, size = 'current') {
    const lineItems = order.line_items || [];
    const itemCount = lineItems.length;
    const defaultThumb = this.getOrderThumbnail(order);

    // For single item, just return a simple image
    if (itemCount <= 1) {
      const cssClass = size === 'current' ? 'skylit-hub__current-thumb' : 'skylit-hub__list-thumb';
      return `
        <img
          src="${this.escapeHtml(sizedThumb(defaultThumb, 160))}"
          alt="${this.escapeHtml(order.product_label || 'Order')}"
          class="${cssClass}"
          loading="lazy"
        >
      `;
    }

    // For multiple items, show stacked thumbnails (max 3)
    const maxImages = Math.min(itemCount, 3);
    const images = [];

    for (let i = 0; i < maxImages; i++) {
      const item = lineItems[i];
      const imgSrc = sizedThumb(item?.image || defaultThumb, 160);
      images.push(imgSrc);
    }

    const wrapperClass = size === 'current' ? 'skylit-hub__stacked-thumbs' : 'skylit-hub__stacked-thumbs skylit-hub__stacked-thumbs--small';

    return `
      <div class="${wrapperClass}" data-count="${itemCount}">
        ${images.map((src, idx) => `
          <img
            src="${this.escapeHtml(src)}"
            alt="Item ${idx + 1}"
            class="skylit-hub__stacked-img"
            style="--stack-index: ${idx};"
            loading="lazy"
          >
        `).join('')}
        ${itemCount > 3 ? `<span class="skylit-hub__stacked-more">+${itemCount - 3}</span>` : ''}
      </div>
    `;
  }

  renderCurrentOrderCard(order) {
    const statusInfo = this.getOrderStatus(order);
    const itemCount = order.line_items?.length || 1;

    // Format order number to show Shopify order
    const displayOrderNum = this.formatOrderNumber(order);

    let ebookBtn = '';
    if (order.has_ebook && order.ebook_url) {
      ebookBtn = `
        <a href="${this.escapeHtml(order.ebook_url)}" class="skylit-hub__current-ebook" target="_blank" rel="noopener" onclick="event.stopPropagation();">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          ${order.product_type === 'moon_map' ? 'View Lunar Almanac' : 'View Constellation Legend'}
        </a>
      `;
    }

    return `
      <div class="skylit-hub__current-card" data-order-id="${this.escapeHtml(order.order_id)}" style="cursor: pointer;">
        <div class="skylit-hub__current-header">
          <div class="skylit-hub__current-info">
            <p class="skylit-hub__current-status">${this.escapeHtml(statusInfo.text)}</p>
            <p class="skylit-hub__current-order-num">Order ${this.escapeHtml(displayOrderNum)}</p>
            <p class="skylit-hub__current-items">${itemCount} item${itemCount !== 1 ? 's' : ''}</p>
          </div>
          ${this.renderStackedThumbnails(order, 'current')}
        </div>
        <div class="skylit-hub__progress">
          <div class="skylit-hub__progress-segment ${statusInfo.progress >= 1 ? 'is-complete' : ''}"></div>
          <div class="skylit-hub__progress-segment ${statusInfo.progress >= 2 ? 'is-complete' : ''}"></div>
          <div class="skylit-hub__progress-segment ${statusInfo.progress >= 3 ? 'is-complete' : ''}"></div>
        </div>
        ${ebookBtn}
      </div>
    `;
  }

  renderListItem(order) {
    const statusInfo = this.getOrderStatus(order);
    const itemCount = order.line_items?.length || 1;
    const displayOrderNum = this.formatOrderNumber(order);

    return `
      <li class="skylit-hub__list-item" data-order-id="${this.escapeHtml(order.order_id)}">
        ${this.renderStackedThumbnails(order, 'list')}
        <div class="skylit-hub__list-info">
          <p class="skylit-hub__list-status">${this.escapeHtml(statusInfo.text)}</p>
          <p class="skylit-hub__list-order-num">${this.escapeHtml(displayOrderNum)}</p>
          <p class="skylit-hub__list-items">${itemCount} item${itemCount !== 1 ? 's' : ''}</p>
        </div>
        <span class="skylit-hub__list-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </span>
      </li>
    `;
  }

  async fetchShopifyOrderDetails(order) {
    // Only fetch if order has a Shopify order ID
    if (!order.shopify_order_id) {
      return null;
    }

    try {
      // Ensure we have a valid session token
      const token = await this.ensureSessionToken();

      // Extract base API URL (remove '/customer-orders/' if present)
      const baseApiUrl = this.apiUrl.replace(/\/customer-orders\/?$/, '/');

      // Build URL with token parameter
      const params = new URLSearchParams();
      params.set('token', token);
      const url = `${baseApiUrl}shopify-order/${order.shopify_order_id}/?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const shopifyData = await response.json();
      return shopifyData;
    } catch (error) {
      return null;
    }
  }

  async showOrderDetail(order) {
    if (!this.ordersContainer) return;

    // Fetch full Shopify order details including fulfillments
    const shopifyDetails = await this.fetchShopifyOrderDetails(order);

    // Merge Shopify fulfillment data into order object
    if (shopifyDetails && shopifyDetails.fulfillments) {
      order.fulfillments = shopifyDetails.fulfillments;
      order.fulfillment_status = shopifyDetails.fulfillment_status;
    }

    const statusInfo = this.getOrderStatus(order);
    const statusText = statusInfo.text.toUpperCase();
    const progress = statusInfo.progress;

    // Format dates
    const orderDate = order.created_at ? this.formatDate(order.created_at) : 'Date not available';
    const updateDate = order.updated_at ? this.formatDate(order.updated_at) : orderDate;

    // Format order number for display
    const displayOrderNum = `Order ${this.formatOrderNumber(order)}`;

    // Build customization details
    const customDetails = [];
    if (order.location) {
      customDetails.push(`<strong>Location:</strong> ${this.escapeHtml(order.location)}`);
    }
    if (order.date_text) {
      customDetails.push(`<strong>Date:</strong> ${this.escapeHtml(this.formatDateText(order.date_text))}`);
    }
    if (order.time_text) {
      const formattedTime = this.formatTimeText(order.time_text, order.time_shown);
      customDetails.push(`<strong>Time:</strong> ${this.escapeHtml(formattedTime)}`);
    }
    if (order.title) {
      customDetails.push(`<strong>Title:</strong> ${this.escapeHtml(order.title)}`);
    }
    if (order.message) {
      // Replace <br> tags with newlines, then escape everything else
      const messageWithBreaks = order.message.replace(/<br\s*\/?>/gi, '\n');
      const escapedMessage = this.escapeHtml(messageWithBreaks).replace(/\n/g, '<br>');
      customDetails.push(`<strong>Message:</strong> ${escapedMessage}`);
    }
    if (order.caption) {
      customDetails.push(`<strong>Caption:</strong> ${this.escapeHtml(order.caption)}`);
    }
    if (order.ribbon_colour) {
      customDetails.push(`<strong>Ribbon Colour:</strong> ${this.escapeHtml(order.ribbon_colour)}`);
    }
    if (order.gift_wrap_theme) {
      customDetails.push(`<strong>Gift Wrap:</strong> ${this.escapeHtml(order.gift_wrap_theme)}`);
    }
    // Gift note gets its own section below (editable when unprinted), not a
    // plain customDetails row — see renderGiftNoteSection.

    const customizationHtml = customDetails.length > 0 ? `
      <div class="skylit-hub__detail-section">
        <h4 class="skylit-hub__detail-section-title">Your Customization</h4>
        <div class="skylit-hub__detail-customization">
          ${customDetails.map(detail => `<p class="skylit-hub__detail-custom-row">${detail}</p>`).join('')}
        </div>
      </div>
    ` : '';

    // Ebook button
    // Debug: console.log('[CustomerHub] Ebook data for order:', {
    //   order_id: order.order_id,
    //   has_ebook: order.has_ebook,
    //   ebook_url: order.ebook_url,
    //   product_type: order.product_type
    // });

    let ebookBtn = '';
    if (order.has_ebook && order.ebook_url) {
      ebookBtn = `
        <a href="${this.escapeHtml(order.ebook_url)}" class="skylit-hub__detail-btn skylit-hub__detail-btn--primary" target="_blank" rel="noopener">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          ${order.product_type === 'moon_map' ? 'View Lunar Almanac' : 'View Constellation Legend'}
        </a>
      `;
    }

    this.ordersContainer.innerHTML = `
      <div class="skylit-hub__detail">
        <button class="skylit-hub__detail-back" onclick="window.skylitHub.renderOrders(window.skylitHub.ordersData.orders)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Order details
        </button>

        <div class="skylit-hub__detail-status">
          <p class="skylit-hub__detail-status-text">${statusText}</p>
          <p class="skylit-hub__detail-date">Ordered ${orderDate}</p>
          <p class="skylit-hub__detail-order-num">${displayOrderNum}</p>
        </div>

        <div class="skylit-hub__detail-progress">
          <div class="skylit-hub__detail-progress-segment ${progress >= 1 ? 'is-complete' : ''}">
            <span class="skylit-hub__progress-label">Ordered</span>
          </div>
          <div class="skylit-hub__detail-progress-segment ${progress >= 2 ? 'is-complete' : ''}">
            <span class="skylit-hub__progress-label">Confirmed</span>
          </div>
          <div class="skylit-hub__detail-progress-segment ${progress >= 3 ? 'is-complete' : ''}">
            <span class="skylit-hub__progress-label">Shipped</span>
          </div>
          <div class="skylit-hub__detail-progress-segment ${progress >= 4 ? 'is-complete' : ''}">
            <span class="skylit-hub__progress-label">Delivered</span>
          </div>
        </div>

        <div class="skylit-hub__detail-update">
          <span style="color: var(--hub-text-secondary); opacity: 0.8;">Last update</span>
          <span style="font-weight: 500; color: var(--hub-text);">${updateDate}</span>
        </div>

        <div class="skylit-hub__detail-actions">
          ${ebookBtn}
          <a href="/collections/all" class="skylit-hub__detail-btn ${order.has_ebook ? 'skylit-hub__detail-btn--secondary' : 'skylit-hub__detail-btn--primary'}">
            Buy again
          </a>
          ${this.renderTrackingButton(order)}
          <button onclick="window.skylitHub.switchTab('contact')" class="skylit-hub__detail-btn skylit-hub__detail-btn--outline">
            Get help
          </button>
        </div>

        ${this.renderLineItems(order)}

        ${customizationHtml}

        ${this.renderGiftNoteSection(order)}

        ${this.renderOrderSummary(order)}

        ${this.renderShippingAddress(order)}

        <div class="skylit-hub__detail-section">
          <h4 class="skylit-hub__detail-section-title">Need Help?</h4>
          <p class="skylit-hub__detail-address">
            Questions about your order? Use the <a href="#" onclick="event.preventDefault(); window.skylitHub.switchTab('contact');" style="color: var(--hub-primary); text-decoration: underline;">Contact tab</a> above to get in touch.
          </p>
        </div>
      </div>
    `;

    this.initGiftNoteEditor(order);
  }

  // Gift notes: seen at checkout, editable up to the point each is printed
  // (n.editable, sourced from gift_notes.printed_at IS NULL — see
  // app/api/customer-orders/route.ts). An order can carry SEVERAL notes (one
  // per paired print) — order.gift_notes is always an array.
  //
  // A single-note order (the overwhelming majority) renders EXACTLY as
  // before this became an array: no label, no "Note 1 of 1", one box. Only
  // once there's more than one note does each get its own labelled box, so
  // several notes never read as identical unlabelled boxes.
  //
  // n.editable === null means we have no D1-backed note to speak to (legacy
  // cart-property-only note): show it read-only with no printed/editable
  // claim either way — same as the pre-array fallback behaviour.
  renderGiftNoteSection(order) {
    const notes = Array.isArray(order.gift_notes) ? order.gift_notes : [];
    if (notes.length === 0) return '';

    if (notes.length === 1) {
      return `
        <div class="skylit-hub__detail-section">
          <h4 class="skylit-hub__detail-section-title">Gift Note</h4>
          ${this.renderGiftNoteBox(order, notes[0])}
        </div>
      `;
    }

    const boxes = notes.map((n, i) => `
      <div class="skylit-hub__gift-note-item">
        <p class="skylit-hub__gift-note-label">${this.escapeHtml(n.label || `Note ${i + 1} of ${notes.length}`)}</p>
        ${this.renderGiftNoteBox(order, n)}
      </div>
    `).join('');

    return `
      <div class="skylit-hub__detail-section">
        <h4 class="skylit-hub__detail-section-title">Gift Notes (${notes.length})</h4>
        <div class="skylit-hub__gift-note-list">${boxes}</div>
      </div>
    `;
  }

  // One note's box: an editable form, a read-only "already printed" block,
  // or a plain read-only block for a legacy (non-D1-backed) note.
  renderGiftNoteBox(order, n) {
    const note = n.note || '';
    const editable = n.editable === true;
    const knownPrinted = n.editable === false;
    const noteIdAttr = n.id != null ? this.escapeHtml(String(n.id)) : '';

    if (editable) {
      const count = note.length;
      return `
        <form class="skylit-hub__gift-note-form" data-gift-note-form data-note-id="${noteIdAttr}">
          <div class="skylit-hub__form-group">
            <textarea
              class="skylit-hub__form-textarea"
              data-gift-note-input
              maxlength="250"
              rows="3"
              placeholder="A few warm words for the recipient…"
            >${this.escapeHtml(note)}</textarea>
            <div class="skylit-hub__gift-note-meta">
              <span data-gift-note-count>${count}</span>/250
            </div>
          </div>
          <div class="skylit-hub__form-submit">
            <button type="submit" class="skylit-hub__detail-btn skylit-hub__detail-btn--primary" data-gift-note-save>
              Save note
            </button>
          </div>
          <div class="skylit-hub__form-status" data-gift-note-status style="display: none;"></div>
        </form>
      `;
    }

    return `
      <p class="skylit-hub__detail-custom-row">${this.escapeHtml(note)}</p>
      ${knownPrinted ? `<p class="skylit-hub__gift-note-locked">Already printed, changes can no longer be made.</p>` : ''}
    `;
  }

  initGiftNoteEditor(order) {
    const forms = this.ordersContainer?.querySelectorAll('[data-gift-note-form]');
    if (!forms) return;

    forms.forEach((form) => {
      const noteId = form.getAttribute('data-note-id');
      const textarea = form.querySelector('[data-gift-note-input]');
      const counter = form.querySelector('[data-gift-note-count]');
      textarea?.addEventListener('input', () => {
        if (counter) counter.textContent = String(textarea.value.length);
      });

      form.addEventListener('submit', (e) => this.handleGiftNoteSave(e, order, noteId));
    });
  }

  async handleGiftNoteSave(e, order, noteId) {
    e.preventDefault();

    const form = e.target;
    const textarea = form.querySelector('[data-gift-note-input]');
    const statusDiv = form.querySelector('[data-gift-note-status]');
    const saveBtn = form.querySelector('[data-gift-note-save]');
    const note = textarea ? textarea.value : '';

    if (note.length > 250) {
      if (statusDiv) {
        statusDiv.className = 'skylit-hub__form-status skylit-hub__form-status--error';
        statusDiv.textContent = 'Gift notes can be at most 250 characters.';
        statusDiv.style.display = 'block';
      }
      return;
    }

    const originalBtnText = saveBtn ? saveBtn.innerHTML : '';
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.innerHTML = 'Saving…';
    }
    if (statusDiv) statusDiv.style.display = 'none';

    try {
      const token = await this.ensureSessionToken();
      const baseApiUrl = this.apiUrl.replace(/\/customer-orders\/?$/, '/');

      const response = await fetch(`${baseApiUrl}customer-gift-note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          note_id: noteId != null ? Number(noteId) : null,
          shopify_order_id: order.shopify_order_id,
          note,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      // Keep the in-memory order cache in sync — update ONLY the note that
      // was actually saved (an order can carry several), so navigating back
      // to the list and re-opening this order shows the saved note without
      // touching its siblings.
      const applySavedNote = (o) => {
        const target = Array.isArray(o?.gift_notes)
          ? o.gift_notes.find((n) => String(n.id) === String(noteId))
          : null;
        if (target) target.note = note;
      };
      applySavedNote(order);
      const cached = this.ordersData?.orders?.find(o => o.order_id === order.order_id);
      if (cached && cached !== order) applySavedNote(cached);

      if (statusDiv) {
        statusDiv.className = 'skylit-hub__form-status skylit-hub__form-status--success';
        statusDiv.textContent = 'Gift note saved.';
        statusDiv.style.display = 'block';
      }
    } catch (error) {
      console.error('[CustomerHub] Failed to save gift note:', error);
      // Never silently discard what the customer typed — the textarea keeps
      // it as-is, and the error is shown inline so they know to retry.
      if (statusDiv) {
        statusDiv.className = 'skylit-hub__form-status skylit-hub__form-status--error';
        statusDiv.textContent = (error && error.message) || 'Failed to save gift note. Please try again.';
        statusDiv.style.display = 'block';
      }
    } finally {
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.innerHTML = originalBtnText;
      }
    }
  }

  renderOrderSummary(order) {
    // If we don't have Shopify pricing data, don't show summary
    if (!order.total_price) {
      return '';
    }

    const currency = order.currency || 'GBP';
    const currencySymbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;

    // Check if there are any discounts
    const hasDiscounts = order.discount_applications && order.discount_applications.length > 0;
    const totalDiscounts = parseFloat(order.total_discounts || 0);

    // Calculate original subtotal if discounts exist
    const subtotal = parseFloat(order.subtotal_price);
    const originalSubtotal = hasDiscounts ? subtotal + totalDiscounts : subtotal;

    return `
      <div class="skylit-hub__detail-section">
        <h4 class="skylit-hub__detail-section-title">Order Summary</h4>
        <div class="skylit-hub__detail-summary">
          ${hasDiscounts ? `
          <div class="skylit-hub__detail-summary-row skylit-hub__detail-summary-row--original">
            <span>Original Subtotal:</span>
            <span>${currencySymbol}${originalSubtotal.toFixed(2)}</span>
          </div>
          ${order.discount_applications.map(discount => `
          <div class="skylit-hub__detail-summary-row skylit-hub__detail-summary-row--discount">
            <span>${this.escapeHtml(this.decodeEntities(discount.title))}:</span>
            <span>-${currencySymbol}${parseFloat(discount.total_allocated_amount).toFixed(2)}</span>
          </div>
          `).join('')}
          <div class="skylit-hub__detail-summary-row">
            <span>Subtotal:</span>
            <span>${currencySymbol}${subtotal.toFixed(2)}</span>
          </div>
          ` : `
          <div class="skylit-hub__detail-summary-row">
            <span>Subtotal:</span>
            <span>${currencySymbol}${subtotal.toFixed(2)}</span>
          </div>
          `}
          ${order.shipping_price ? `
          <div class="skylit-hub__detail-summary-row">
            <span>Shipping:</span>
            <span>${currencySymbol}${parseFloat(order.shipping_price).toFixed(2)}</span>
          </div>
          ` : ''}
          ${order.tax_price ? `
          <div class="skylit-hub__detail-summary-row">
            <span>Tax:</span>
            <span>${currencySymbol}${parseFloat(order.tax_price).toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="skylit-hub__detail-summary-row is-total">
            <span>Total:</span>
            <span>${currencySymbol}${parseFloat(order.total_price).toFixed(2)}</span>
          </div>
        </div>
      </div>
    `;
  }

  renderLineItems(order) {
    const lineItems = order.line_items || [];

    if (lineItems.length === 0) {
      // Fallback for orders without line item data
      const thumbnail = this.getOrderThumbnail(order);
      const productName = order.product_label || 'Personalised Star Map';
      return `
        <div class="skylit-hub__detail-section">
          <h4 class="skylit-hub__detail-section-title">Your Order</h4>
          <div class="skylit-hub__detail-product">
            <img src="${this.escapeHtml(sizedThumb(thumbnail, 160))}" alt="${this.escapeHtml(productName)}" class="skylit-hub__detail-product-img" loading="lazy"${zoomAttr(thumbnail)}>
            <div class="skylit-hub__detail-product-info">
              <p class="skylit-hub__detail-product-name">${this.escapeHtml(productName)}</p>
            </div>
          </div>
        </div>
      `;
    }

    const itemsHtml = lineItems.map(item => {
      const itemImage = sizedThumb(item.image || this.getOrderThumbnail(order), 160);
      const itemTitle = item.title || 'Product';
      const variantTitle = item.variant_title || '';
      const quantity = item.quantity || 1;
      const price = item.price ? `£${parseFloat(item.price).toFixed(2)}` : '';

      return `
        <div class="skylit-hub__detail-product">
          <img src="${this.escapeHtml(itemImage)}" alt="${this.escapeHtml(itemTitle)}" class="skylit-hub__detail-product-img" loading="lazy"${zoomAttr(itemImage)}>
          <div class="skylit-hub__detail-product-info">
            <p class="skylit-hub__detail-product-name">${this.escapeHtml(itemTitle)}</p>
            ${variantTitle ? `<p class="skylit-hub__detail-product-variant">${this.escapeHtml(variantTitle)}</p>` : ''}
            <p class="skylit-hub__detail-product-meta">
              ${quantity > 1 ? `Qty: ${quantity}` : ''}
              ${quantity > 1 && price ? ' · ' : ''}
              ${price}
            </p>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="skylit-hub__detail-section">
        <h4 class="skylit-hub__detail-section-title">Your Order (${lineItems.length} item${lineItems.length !== 1 ? 's' : ''})</h4>
        ${itemsHtml}
      </div>
    `;
  }

  renderTrackingButton(order) {
    const fulfillments = order.fulfillments || [];
    const hasFulfillment = fulfillments.length > 0 &&
                          fulfillments[0].tracking_number &&
                          fulfillments[0].tracking_number.trim() !== '';

    // Debug logging
    console.log('Tracking button debug:', {
      order_id: order.order_id,
      fulfillments: fulfillments,
      fulfillments_length: fulfillments.length,
      first_fulfillment: fulfillments[0],
      tracking_number: fulfillments[0]?.tracking_number,
      tracking_number_type: typeof fulfillments[0]?.tracking_number,
      tracking_number_trimmed: fulfillments[0]?.tracking_number?.trim(),
      hasFulfillment: hasFulfillment,
      fulfillment_status: order.fulfillment_status
    });

    // If order is marked as fulfilled but no tracking available
    if (!hasFulfillment && (order.fulfillment_status === 'fulfilled' || order.fulfillment_status === 'partial')) {
      return `
        <div class="skylit-hub__tracking-note">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <p>Tracking information will be sent to your email once available. Please check your inbox or <a href="/contact">contact us</a> for updates.</p>
        </div>
      `;
    }

    if (!hasFulfillment) {
      return `
        <button class="skylit-hub__detail-btn skylit-hub__detail-btn--outline" disabled style="opacity: 0.5; cursor: not-allowed;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="3" width="15" height="13"></rect>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
          </svg>
          Track Shipment
        </button>
      `;
    }

    return `
      <button class="skylit-hub__detail-btn skylit-hub__detail-btn--outline" onclick="window.skylitHub.showTracking('${this.escapeHtml(order.order_id)}')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
        Track Shipment
      </button>
    `;
  }

  renderShippingAddress(order) {
    const shipping = order.shipping_address;
    if (!shipping || !shipping.name) {
      return '';
    }

    return `
      <div class="skylit-hub__detail-section">
        <h4 class="skylit-hub__detail-section-title">Shipping Address</h4>
        <div class="skylit-hub__detail-address">
          <strong>${this.escapeHtml(shipping.name)}</strong><br>
          ${shipping.address1 ? this.escapeHtml(shipping.address1) + '<br>' : ''}
          ${shipping.address2 ? this.escapeHtml(shipping.address2) + '<br>' : ''}
          ${shipping.city ? this.escapeHtml(shipping.city) + '<br>' : ''}
          ${shipping.province ? this.escapeHtml(shipping.province) + ' ' : ''}${shipping.zip ? this.escapeHtml(shipping.zip) + '<br>' : ''}
          ${shipping.country ? this.escapeHtml(shipping.country) : ''}
          ${shipping.phone ? '<br>' + this.escapeHtml(shipping.phone) : ''}
        </div>
      </div>
    `;
  }

  showTracking(orderId) {
    const order = this.ordersData.orders.find(o => o.order_id === orderId);
    if (!order || !order.fulfillments || order.fulfillments.length === 0) {
      return;
    }

    const fulfillment = order.fulfillments[0]; // Get first fulfillment
    const trackingNumber = fulfillment.tracking_number;
    const trackingUrl = fulfillment.tracking_url;
    const trackingCompany = fulfillment.tracking_company || 'Shipping Carrier';

    // Create tracking modal overlay
    const modalHtml = `
      <div class="skylit-hub__tracking-modal" id="tracking-modal">
        <div class="skylit-hub__tracking-backdrop" onclick="window.skylitHub.closeTracking()"></div>
        <div class="skylit-hub__tracking-panel">
          <div class="skylit-hub__tracking-header">
            <h3 class="skylit-hub__tracking-title">Track Your Shipment</h3>
            <button class="skylit-hub__tracking-close" onclick="window.skylitHub.closeTracking()">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="skylit-hub__tracking-content">
            <div class="skylit-hub__tracking-info">
              <div class="skylit-hub__tracking-row">
                <span class="skylit-hub__tracking-label">Tracking Number:</span>
                <span class="skylit-hub__tracking-value">${this.escapeHtml(trackingNumber)}</span>
              </div>
              <div class="skylit-hub__tracking-row">
                <span class="skylit-hub__tracking-label">Carrier:</span>
                <span class="skylit-hub__tracking-value">${this.escapeHtml(trackingCompany)}</span>
              </div>
              <div class="skylit-hub__tracking-row">
                <span class="skylit-hub__tracking-label">Order:</span>
                <span class="skylit-hub__tracking-value">${this.formatOrderNumber(order)}</span>
              </div>
            </div>

            ${/royal\s*mail/i.test(trackingCompany) ? `
            <p class="skylit-hub__tracking-note">We take Royal Mail parcels to the Manchester delivery depot ourselves, so they aren't scanned when we hand them over. The first tracking update usually appears overnight, sometimes not until later the next day. If it still looks quiet, email us and we'll double-check for you.</p>
            ` : ''}

            ${trackingUrl ? `
            <div class="skylit-hub__tracking-actions">
              <a href="${this.escapeHtml(trackingUrl)}" target="_blank" rel="noopener" class="skylit-hub__tracking-btn skylit-hub__tracking-btn--primary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                Track on ${this.escapeHtml(trackingCompany)}
              </a>
            </div>
            ` : ''}

            <div class="skylit-hub__tracking-timeline">
              <h4 class="skylit-hub__tracking-timeline-title">Shipment Status</h4>
              ${this.renderTrackingTimeline(fulfillment, order)}
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert modal into page
    const existingModal = document.getElementById('tracking-modal');
    if (existingModal) {
      existingModal.remove();
    }

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    document.body.style.overflow = 'hidden';

    // Focus close button for accessibility
    setTimeout(() => {
      document.querySelector('.skylit-hub__tracking-close')?.focus();
    }, 100);
  }

  closeTracking() {
    const modal = document.getElementById('tracking-modal');
    if (modal) {
      modal.remove();
      document.body.style.overflow = '';
    }
  }

  renderTrackingTimeline(fulfillment, order) {
    const events = [];

    // Check if we have actual tracking events from the carrier (e.g., Royal Mail)
    const trackingEvents = fulfillment.tracking_events || [];
    const hasRealTrackingEvents = trackingEvents.length > 0;

    if (hasRealTrackingEvents) {
      // Use actual tracking events from carrier
      trackingEvents.forEach(event => {
        events.push({
          status: event.status || 'Update',
          date: event.happened_at,
          description: event.message || '',
          location: event.city || event.province || event.country || null,
          completed: true
        });
      });
    } else {
      // Fallback to basic timeline based on shipment_status
      const shipmentStatus = fulfillment.shipment_status?.toLowerCase() || '';
      const isDelivered = shipmentStatus === 'delivered';
      const isInTransit = ['in_transit', 'out_for_delivery', 'attempted_delivery'].includes(shipmentStatus);

      // Add order placed event
      if (order.created_at) {
        events.push({
          status: 'Order Placed',
          date: order.created_at,
          description: 'Your order has been received and is being processed',
          completed: true
        });
      }

      // Add fulfillment event
      if (fulfillment.created_at) {
        events.push({
          status: 'Shipped',
          date: fulfillment.created_at,
          description: `Package shipped via ${fulfillment.tracking_company || 'Royal Mail'}`,
          completed: true
        });
      }

      // Add in-transit event only if actually in transit
      if (isInTransit) {
        events.push({
          status: shipmentStatus === 'out_for_delivery' ? 'Out for Delivery' : 'In Transit',
          date: fulfillment.updated_at,
          description: shipmentStatus === 'out_for_delivery' ? 'Package is out for delivery' : 'Package is on its way',
          completed: true
        });
      }

      // Add delivery event only if actually delivered
      if (isDelivered) {
        events.push({
          status: 'Delivered',
          date: fulfillment.updated_at,
          description: 'Package has been delivered',
          completed: true
        });
      }
    }

    return `
      <div class="skylit-hub__timeline">
        ${events.map((event, index) => `
          <div class="skylit-hub__timeline-item ${event.completed ? 'is-completed' : ''}">
            <div class="skylit-hub__timeline-marker">
              ${event.completed ? `
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ` : `
                <div class="skylit-hub__timeline-dot"></div>
              `}
            </div>
            <div class="skylit-hub__timeline-content">
              <p class="skylit-hub__timeline-status">${this.escapeHtml(event.status)}</p>
              <p class="skylit-hub__timeline-description">${this.escapeHtml(event.description)}</p>
              ${event.location ? `<p class="skylit-hub__timeline-location">📍 ${this.escapeHtml(event.location)}</p>` : ''}
              ${event.date ? `<p class="skylit-hub__timeline-date">${this.formatDate(event.date)}</p>` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  formatDate(dateStr) {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  daysUntilExpiry(dateStr) {
    // Backend timestamps are UTC "YYYY-MM-DD HH:MM:SS"; Date() would parse
    // that as LOCAL time, so normalise to ISO+Z before diffing.
    try {
      const iso = typeof dateStr === 'string' && !dateStr.includes('T')
        ? dateStr.replace(' ', 'T') + 'Z'
        : dateStr;
      const expires = new Date(iso);
      if (isNaN(expires.getTime())) return null;
      const diff = expires.getTime() - Date.now();
      if (diff < 0) return null;
      return Math.floor(diff / 86400000);
    } catch (e) {
      return null;
    }
  }

  formatDateText(dateText) {
    /**
     * Format date_text from backend which may use zero-indexed months.
     * Handles formats like:
     * - "17 0 2026" (day month year with spaces - backend format)
     * - "0/15/2024" (month/day/year with slashes)
     * Also handles already-formatted strings gracefully.
     */
    if (!dateText || typeof dateText !== 'string') {
      return dateText || '';
    }

    // Check if it's an ISO date (e.g., "1996-04-13" - the backend's stored format).
    // Months here are 1-indexed, unlike the two legacy formats below.
    const isoMatch = dateText.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);

      if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
        return new Date(parseInt(year, 10), monthNum - 1, dayNum).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }
    }

    // Check if it's a space-separated format (e.g., "17 0 2026" - backend format)
    // Format: day month year (month is 0-indexed)
    const spaceMatch = dateText.match(/^(\d{1,2})\s+(\d{1,2})\s+(\d{4})$/);

    if (spaceMatch) {
      const [_, day, month, year] = spaceMatch;
      const dayNum = parseInt(day, 10);
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);

      // Backend uses zero-indexed months (0-11)
      // Validate month is in range 0-11
      if (monthNum >= 0 && monthNum <= 11 && dayNum >= 1 && dayNum <= 31) {
        // Create date with zero-indexed month
        const date = new Date(yearNum, monthNum, dayNum);

        // Format as "17 January 2026"
        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }
    }

    // Check if it's a slash-separated format (e.g., "0/15/2024")
    // Format: month/day/year (month is 0-indexed)
    const slashMatch = dateText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    if (slashMatch) {
      const [_, month, day, year] = slashMatch;
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);

      // Backend uses zero-indexed months (0-11)
      // Validate month is in range 0-11
      if (monthNum >= 0 && monthNum <= 11 && dayNum >= 1 && dayNum <= 31) {
        // Create date with zero-indexed month
        const date = new Date(yearNum, monthNum, dayNum);

        // Format as "15 January 2024"
        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }
    }

    // If not numeric format or parsing failed, return as-is (already formatted)
    return dateText;
  }

  formatTimeText(timeText, timeShown = null) {
    /**
     * Format time text with optional (shown/hidden) indicator.
     */
    if (!timeText || !timeText.trim()) {
      return '';
    }

    let formatted = timeText.trim();

    // Append (shown) or (hidden) indicator if timeShown is explicitly set
    if (timeShown === true) {
      formatted += ' (shown)';
    } else if (timeShown === false) {
      formatted += ' (hidden)';
    }

    return formatted;
  }

  formatOrderNumber(order) {
    // Priority 1: Shopify order name (includes prefix/suffix like "SkylitStudio#10001")
    if (order.name && order.name.trim()) {
      return order.name.trim();
    }

    // Priority 2: Shopify order ID with prefix
    if (order.shopify_order_id && order.shopify_order_id.trim()) {
      return `SkylitStudio#${order.shopify_order_id.trim()}`;
    }

    // Priority 3: Extract from order_number if it contains #
    if (order.order_number && order.order_number.includes('#')) {
      return order.order_number.trim();
    }

    // Priority 4: Add prefix to numeric order_number
    if (order.order_number && order.order_number.trim()) {
      const orderNum = order.order_number.trim();
      // If it's just a number, add the prefix
      if (/^\d+$/.test(orderNum)) {
        return `SkylitStudio#${orderNum}`;
      }
      return orderNum.startsWith('#') ? orderNum : `#${orderNum}`;
    }

    // Fallback: Internal order ID (shortened for display)
    const orderId = order.order_id || '';
    if (orderId.length > 12) {
      return `${orderId.substring(0, 12).toUpperCase()}...`;
    }
    return orderId.toUpperCase() || '#N/A';
  }

  renderEmpty() {
    if (!this.ordersContainer) return;

    this.ordersContainer.innerHTML = `
      <div class="skylit-hub__empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M8 12h8"></path>
        </svg>
        <h3>No orders yet</h3>
        <p>Create your personalised star or moon map today</p>
        <a href="/collections/all" class="skylit-hub__shop-btn">Start Shopping</a>
      </div>
    `;
  }

  renderError(message) {
    if (!this.ordersContainer) return;

    this.ordersContainer.innerHTML = `
      <div class="skylit-hub__error">
        <p>${this.escapeHtml(message)}</p>
        <button class="skylit-hub__retry-btn" onclick="window.skylitHub?.loadOrders()">
          Try Again
        </button>
      </div>
    `;
  }

  renderAccount() {
    if (!this.accountContainer) return;

    const initials = this.getInitials();
    const displayName = this.customerName || `${this.customerFirstName} ${this.customerLastName}`.trim() || 'Customer';

    let addressesHtml = '';
    if (this.customerAddresses.length > 0) {
      addressesHtml = `
        <div class="skylit-hub__section-header">Shipping information</div>
        <ul class="skylit-hub__account-links">
          ${this.customerAddresses.map(addr => this.renderAddressItem(addr)).join('')}
        </ul>
      `;
    }

    this.accountContainer.innerHTML = `
      <div class="skylit-hub__account">
        <div class="skylit-hub__account-header">
          <div class="skylit-hub__account-avatar">${this.escapeHtml(initials)}</div>
          <div class="skylit-hub__account-info">
            <h3>${this.escapeHtml(displayName)}</h3>
            <p>${this.escapeHtml(this.customerEmail)}</p>
          </div>
        </div>

        <ul class="skylit-hub__account-links">
          <a href="${this.escapeHtml(this.accountUrl)}" class="skylit-hub__account-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <span>Edit account</span>
            <span class="skylit-hub__list-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </span>
          </a>
        </ul>

        ${addressesHtml}

        <ul class="skylit-hub__account-links" style="margin-top: 24px;">
          <a href="${this.escapeHtml(this.accountUrl)}" class="skylit-hub__account-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span>View account</span>
            <span class="skylit-hub__list-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </span>
          </a>
        </ul>

        <a href="${this.escapeHtml(this.logoutUrl)}" class="skylit-hub__logout-btn">Sign out</a>
      </div>
    `;
  }

  renderAddressItem(address) {
    const line1 = address.address1 || '';
    const city = address.city || '';
    const country = address.country || '';
    const summary = [line1, city, country].filter(Boolean).join(', ');
    const isDefault = address.default;

    return `
      <a href="${this.escapeHtml(this.addressesUrl)}" class="skylit-hub__account-link">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <span>${this.escapeHtml(summary)}${isDefault ? ' (Default)' : ''}</span>
        <span class="skylit-hub__list-arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </span>
      </a>
    `;
  }

  renderContact() {
    if (!this.contactContainer) return;

    const displayName = this.customerName || `${this.customerFirstName} ${this.customerLastName}`.trim();

    this.contactContainer.innerHTML = `
      <div class="skylit-hub__contact">
        <div class="skylit-hub__contact-header">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
          <h3>Contact Support</h3>
          <p>We're here to help with any questions</p>
        </div>

        <form class="skylit-hub__contact-form" id="skylit-hub-contact-form" novalidate>
          <div class="skylit-hub__form-group">
            <label for="contact-name" class="skylit-hub__form-label">Name</label>
            <input
              type="text"
              id="contact-name"
              name="name"
              class="skylit-hub__form-input"
              value="${this.escapeHtml(displayName)}"
              autocomplete="name"
              required
            >
          </div>

          <div class="skylit-hub__form-group">
            <label for="contact-email" class="skylit-hub__form-label">Email</label>
            <input
              type="email"
              id="contact-email"
              name="email"
              class="skylit-hub__form-input"
              value="${this.escapeHtml(this.customerEmail)}"
              autocomplete="email"
              required
            >
          </div>

          <div class="skylit-hub__form-group">
            <label for="contact-subject" class="skylit-hub__form-label">Subject</label>
            <select id="contact-subject" name="subject" class="skylit-hub__form-input" required>
              <option value="">Select a topic...</option>
              <option value="Order Inquiry">Order Inquiry</option>
              <option value="Product Question">Product Question</option>
              <option value="Technical Support">Technical Support</option>
              <option value="Shipping & Delivery">Shipping & Delivery</option>
              <option value="Returns & Exchanges">Returns & Exchanges</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div class="skylit-hub__form-group">
            <label for="contact-message" class="skylit-hub__form-label">Message</label>
            <textarea
              id="contact-message"
              name="message"
              class="skylit-hub__form-textarea"
              rows="6"
              placeholder="How can we help you?"
              required
            ></textarea>
          </div>

          <div class="skylit-hub__form-group">
            <input type="file" id="contact-files" class="skylit-hub__attach-input" multiple accept="image/*,.heic,.heif,application/pdf,.pdf">
            <label for="contact-files" class="skylit-hub__attach-btn">
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              Add photos or files
            </label>
            <p class="skylit-hub__attach-hint">Optional. Photos or PDFs, up to 5 files and 12 MB in total.</p>
            <ul class="skylit-hub__attach-list" id="contact-file-list" hidden></ul>
          </div>

          <div aria-hidden="true" style="position:absolute;left:-9999px;top:-9999px;height:0;overflow:hidden">
            <label for="contact-website">Website</label>
            <input id="contact-website" type="text" name="website" tabindex="-1" autocomplete="off" value="">
          </div>
          <div class="skylit-hub__turnstile" id="contact-turnstile"></div>

          <div class="skylit-hub__form-submit">
            <button type="submit" class="skylit-hub__submit-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              Send Message
            </button>
          </div>

          <div class="skylit-hub__form-status" id="contact-form-status" role="alert" style="display: none;"></div>
        </form>
      </div>
    `;

    // Posts to skylit-admin /api/contact, the same endpoint as /pages/contact
    // (sections/contact-form.liquid): it stores the message in D1, emails hello@
    // a CSS-free notification with the customer as Reply-To, and attaches any
    // photos. Shopify's built-in /contact email breaks Outlook replies in Apple
    // Mail and cannot carry attachments. Spam: Turnstile + honeypot + rate limit.
    this.contactFiles = [];
    this.contactWidgetId = null;
    const form = this.contactContainer.querySelector('#skylit-hub-contact-form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleContactSubmit(e));
      const fileInput = form.querySelector('#contact-files');
      fileInput.addEventListener('change', () => this.addContactFiles(form, fileInput));
      this.loadTurnstile();
    }
  }

  // The contact page loads Turnstile too; only inject the script once.
  loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!this.turnstilePromise) {
      this.turnstilePromise = new Promise((resolve) => {
        const src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        if (!document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile/"]')) {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          document.head.appendChild(script);
        }
        const started = Date.now();
        const poll = () => {
          if (window.turnstile) resolve(window.turnstile);
          else if (Date.now() - started > 15000) resolve(null);
          else setTimeout(poll, 150);
        };
        poll();
      });
    }
    return this.turnstilePromise;
  }

  async getContactToken(form) {
    const turnstile = await this.loadTurnstile();
    const el = form.querySelector('#contact-turnstile');
    if (!turnstile || !el) return '';
    return new Promise((resolve) => {
      let done = false;
      const finish = (token) => { if (!done) { done = true; resolve(token || ''); } };
      try {
        if (this.contactWidgetId === null) {
          this.contactWidgetId = turnstile.render(el, {
            sitekey: '0x4AAAAAAEmu-Nuoj0YK52mZ',
            execution: 'execute',
            appearance: 'interaction-only',
            callback: (token) => this.contactTokenResolve && this.contactTokenResolve(token),
            'error-callback': () => this.contactTokenResolve && this.contactTokenResolve(''),
            'expired-callback': () => this.contactTokenResolve && this.contactTokenResolve(''),
          });
        }
        this.contactTokenResolve = finish;
        turnstile.reset(this.contactWidgetId);
        turnstile.execute(this.contactWidgetId);
      } catch (err) {
        finish('');
      }
      setTimeout(() => finish(''), 20000);
    });
  }

  addContactFiles(form, fileInput) {
    const MAX_FILES = 5;
    const MAX_BYTES = 12 * 1024 * 1024;
    const okName = /\.(jpe?g|png|gif|webp|heic|heif|pdf)$/i;
    const picked = Array.from(fileInput.files || []);
    fileInput.value = '';
    let problem = '';
    for (const f of picked) {
      const okType = (/^image\//.test(f.type) && !/^image\/svg/.test(f.type)) || f.type === 'application/pdf' || okName.test(f.name);
      if (!okType) { problem = `${f.name} is not a photo or PDF, so it cannot be attached.`; continue; }
      if (this.contactFiles.length >= MAX_FILES) { problem = 'You can attach up to 5 files.'; break; }
      const total = this.contactFiles.reduce((n, x) => n + x.size, 0);
      if (total + f.size > MAX_BYTES) { problem = `${f.name} would take the attachments over 12 MB. Please send fewer or smaller files.`; continue; }
      this.contactFiles.push(f);
    }
    this.renderContactFiles(form);
    if (problem) this.showContactStatus(form, problem, 'error');
    else form.querySelector('#contact-form-status').style.display = 'none';
  }

  renderContactFiles(form) {
    const list = form.querySelector('#contact-file-list');
    list.innerHTML = '';
    this.contactFiles.forEach((f, i) => {
      const size = f.size < 1024 * 1024 ? `${Math.max(1, Math.round(f.size / 1024))} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`;
      const li = document.createElement('li');
      li.className = 'skylit-hub__attach-item';
      li.innerHTML = `<span class="skylit-hub__attach-name"></span><span class="skylit-hub__attach-size">${size}</span>`;
      li.querySelector('.skylit-hub__attach-name').textContent = f.name;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'skylit-hub__attach-remove';
      rm.setAttribute('aria-label', `Remove ${f.name}`);
      rm.innerHTML = '&times;';
      rm.addEventListener('click', () => { this.contactFiles.splice(i, 1); this.renderContactFiles(form); });
      li.appendChild(rm);
      list.appendChild(li);
    });
    list.hidden = this.contactFiles.length === 0;
  }

  showContactStatus(form, text, kind) {
    const statusDiv = form.querySelector('#contact-form-status');
    statusDiv.className = `skylit-hub__form-status skylit-hub__form-status--${kind}`;
    statusDiv.textContent = text;
    statusDiv.style.display = 'block';
    clearTimeout(this.contactStatusTimer);
    // Success fades after a while; an error stays until the customer acts on it.
    if (kind === 'success') this.contactStatusTimer = setTimeout(() => { statusDiv.style.display = 'none'; }, 8000);
  }

  async handleContactSubmit(e) {
    e.preventDefault();

    const form = e.target;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const submitBtn = form.querySelector('.skylit-hub__submit-btn');
    const originalBtnText = submitBtn.innerHTML;

    submitBtn.disabled = true;
    submitBtn.innerHTML = 'Sending...';
    form.querySelector('#contact-form-status').style.display = 'none';

    const fallback = 'Something went wrong sending your message. Please email hello@skylitstudio.co.uk instead.';
    try {
      const token = await this.getContactToken(form);
      const subject = form.elements.subject.value;
      const body = new FormData();
      body.append('name', form.elements.name.value.trim());
      body.append('email', form.elements.email.value.trim());
      body.append('phone', '');
      body.append('message', `Subject: ${subject}\nSent from: account hub\n\n${form.elements.message.value.trim()}`);
      body.append('website', form.elements.website.value);
      body.append('turnstile_token', token);
      this.contactFiles.forEach((f) => body.append('attachments', f, f.name));

      const res = await fetch('https://admin.skylitstudio.co.uk/api/contact', { method: 'POST', body });
      if (res.ok) {
        this.showContactStatus(form, 'Message sent. We\'ll get back to you soon.', 'success');
        form.elements.subject.value = '';
        form.elements.message.value = '';
        this.contactFiles = [];
        this.renderContactFiles(form);
        return;
      }
      const data = await res.json().catch(() => ({}));
      const code = data && data.error;
      let text = fallback;
      if (res.status === 429) text = 'Too many messages just now. Please wait a minute and try again.';
      else if (code === 'attachment_type') text = 'Only photos and PDFs can be attached. Please remove the other files and try again.';
      else if (code === 'too_many_attachments') text = 'You can attach up to 5 files.';
      else if (res.status === 413 || code === 'attachments_too_large') text = 'The attachments are over 12 MB. Please send fewer or smaller files.';
      else if (res.status === 400) text = 'Please check your email address and message, then try again.';
      this.showContactStatus(form, text, 'error');
    } catch (error) {
      console.error('[CustomerHub] Contact form error:', error);
      this.showContactStatus(form, fallback, 'error');
    } finally {
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalBtnText;
    }
  }

  renderOffers() {
    if (!this.offersContainer) return;

    if (this.offersLoaded && this.offersData) {
      this.renderOffersContent(this.offersData);
      return;
    }

    this.offersContainer.innerHTML = `
      <div class="skylit-hub__loading">
        <div class="skylit-hub__spinner"></div>
        <p>Loading your offers...</p>
      </div>
    `;

    this.fetchOffers();
  }

  async fetchOffers() {
    try {
      const token = await this.ensureSessionToken();
      const url = new URL('https://admin.skylitstudio.co.uk/api/customer-coupons/');
      url.searchParams.set('token', token);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      this.offersData = data;
      this.offersLoaded = true;
      this.renderOffersContent(data);
    } catch (error) {
      console.error('[CustomerHub] Failed to load offers:', error);
      this.renderOffersError();
    }
  }

  renderOffersContent(data) {
    if (!this.offersContainer) return;

    const yours = Array.isArray(data?.yours) ? data.yours : [];
    const offers = Array.isArray(data?.offers) ? data.offers : [];

    let yoursHtml;
    if (yours.length === 0) {
      yoursHtml = `
        <div class="skylit-hub__offers-empty">
          <p>No personal codes yet. Offers we send you by email will appear here.</p>
        </div>
      `;
    } else {
      yoursHtml = yours.map(coupon => this.renderCouponCard(coupon)).join('');
    }

    let currentOffersHtml = '';
    if (offers.length > 0) {
      currentOffersHtml = `
        <div class="skylit-hub__section-header" style="margin-top: 24px;">Current offers</div>
        ${offers.map(offer => this.renderCurrentOfferCard(offer)).join('')}
      `;
    }

    this.offersContainer.innerHTML = `
      <div class="skylit-hub__offers">
        <div class="skylit-hub__section-header">Yours</div>
        ${yoursHtml}
        ${currentOffersHtml}
      </div>
    `;

    // Wire up copy buttons
    this.offersContainer.querySelectorAll('.skylit-hub__offer-copy-btn').forEach(btn => {
      btn.addEventListener('click', () => this.copyOfferCode(btn));
    });
  }

  renderCouponCard(coupon) {
    const name = coupon.name || 'Special offer';
    const code = coupon.code || '';
    let statusLine;

    if (coupon.displayState === 'redeemed') {
      statusLine = coupon.redeemed_at ? `Used on ${this.formatDate(coupon.redeemed_at)}` : 'Used';
    } else if (coupon.displayState === 'expired') {
      statusLine = 'Expired';
    } else if (coupon.expires_at) {
      const days = this.daysUntilExpiry(coupon.expires_at);
      if (days === 0) {
        statusLine = 'Expires today';
      } else if (days === 1) {
        statusLine = 'Expires tomorrow';
      } else if (days !== null && days > 1) {
        statusLine = `${days} days left · valid until ${this.formatDate(coupon.expires_at)}`;
      } else {
        statusLine = `Valid until ${this.formatDate(coupon.expires_at)}`;
      }
    } else {
      statusLine = 'Active';
    }

    const stateClass = coupon.displayState === 'redeemed' ? 'is-redeemed'
      : coupon.displayState === 'expired' ? 'is-expired' : 'is-active';

    return `
      <div class="skylit-hub__offer-card ${stateClass}">
        <p class="skylit-hub__offer-name">${this.escapeHtml(name)}</p>
        <div class="skylit-hub__offer-code-row">
          <span class="skylit-hub__offer-code">${this.escapeHtml(code)}</span>
          <button type="button" class="skylit-hub__offer-copy-btn" data-code="${this.escapeHtml(code)}">Copy</button>
        </div>
        <p class="skylit-hub__offer-status">${this.escapeHtml(statusLine)}</p>
        ${coupon.terms ? `<p class="skylit-hub__offer-terms">${this.escapeHtml(coupon.terms)}</p>` : ''}
      </div>
    `;
  }

  renderCurrentOfferCard(offer) {
    return `
      <div class="skylit-hub__offer-card skylit-hub__offer-card--public">
        <p class="skylit-hub__offer-name">${this.escapeHtml(offer.name || 'Special offer')}</p>
        ${offer.public_blurb ? `<p class="skylit-hub__offer-blurb">${this.escapeHtml(offer.public_blurb)}</p>` : ''}
        ${offer.terms ? `<p class="skylit-hub__offer-terms">${this.escapeHtml(offer.terms)}</p>` : ''}
      </div>
    `;
  }

  copyOfferCode(btn) {
    const code = btn.dataset.code || '';
    if (!code) return;

    const originalText = btn.textContent;
    const flashSuccess = () => {
      btn.textContent = 'Copied';
      btn.classList.add('is-copied');
      btn.classList.remove('is-copy-failed');
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('is-copied');
      }, 1500);
    };
    const flashFailure = () => {
      btn.textContent = 'Copy failed, select manually';
      btn.classList.add('is-copy-failed');
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('is-copy-failed');
      }, 2000);
    };
    const copyWithFallback = () => {
      // execCommand-based fallback for non-secure contexts and older Safari,
      // where navigator.clipboard is undefined entirely.
      try {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
        const succeeded = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (succeeded) {
          flashSuccess();
        } else {
          flashFailure();
        }
      } catch (error) {
        console.error('[CustomerHub] Copy fallback failed:', error);
        flashFailure();
      }
    };

    // navigator.clipboard can be undefined (non-secure context, older Safari),
    // and touching .writeText on undefined throws synchronously, bypassing
    // any .catch() chained onto it. Guard before calling, and fall back to
    // execCommand('copy') both when the API is missing and when it rejects.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(flashSuccess).catch((error) => {
        console.error('[CustomerHub] Copy failed:', error);
        copyWithFallback();
      });
    } else {
      copyWithFallback();
    }
  }

  renderOffersError() {
    if (!this.offersContainer) return;

    this.offersContainer.innerHTML = `
      <div class="skylit-hub__error">
        <p>Unable to load offers. Please try again.</p>
        <button type="button" class="skylit-hub__retry-btn skylit-hub__offers-retry-btn">
          Try Again
        </button>
      </div>
    `;

    const retryBtn = this.offersContainer.querySelector('.skylit-hub__offers-retry-btn');
    retryBtn?.addEventListener('click', () => this.renderOffers());
  }

  getInitials() {
    const first = this.customerFirstName?.charAt(0) || '';
    const last = this.customerLastName?.charAt(0) || '';
    if (first || last) {
      return (first + last).toUpperCase();
    }
    // Fallback to email initial
    return this.customerEmail?.charAt(0)?.toUpperCase() || '?';
  }

  getOrderThumbnail(order) {
    if (order.thumbnail_url) return order.thumbnail_url;

    // Default thumbnails by product type
    const defaults = {
      star_map: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/BLACK_FRAME_-_Skylit_Studio_-_Personalised_Star_Map_128x128.jpg?v=1752142686',
      moon_map: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/BLACK_FRAME_-_Skylit_Studio_-_Personalised_Star_Map_128x128.jpg?v=1752142686',
      hor_moon_map: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/BLACK_FRAME_-_Skylit_Studio_-_Personalised_Star_Map_128x128.jpg?v=1752142686',
    };
    return defaults[order.product_type] || defaults.star_map;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // Shopify hands some order fields (e.g. automatic-discount titles) back
  // already HTML-escaped, so escapeHtml alone double-escapes and the customer
  // sees a literal "&amp;". Decode once, then escape for safe insertion.
  decodeEntities(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.innerHTML = str;
    return div.textContent;
  }
}

/**
 * Draw an order thumbnail through Cloudflare's image resizer.
 *
 * The images the hub shows are cart mockups straight out of R2 — one measured
 * at 162,434 bytes. They are drawn at 48-72 CSS px, stacked up to three per
 * order across a list of twenty, so serving them raw would pull megabytes into
 * a drawer that is mostly opened on a phone. The same file at width=160 is
 * 3,391 bytes.
 *
 * Only our own host is rewritten. Shopify's CDN already resizes via img_url,
 * and sending Cloudflare to fetch a third-party URL buys nothing. Re-wrapping
 * an already-resized URL is a no-op rather than a nested /cdn-cgi/image/,
 * which Cloudflare answers with a 400.
 */
/* @lm-test-export:start sizedThumb */
function sizedThumb(url, width) {
  if (!url || typeof url !== 'string') return url;
  if (url.indexOf('/cdn-cgi/image/') !== -1) return url;
  var marker = 'https://admin.skylitstudio.co.uk/';
  if (url.indexOf(marker) !== 0) return url;
  return marker + 'cdn-cgi/image/width=' + width + ',quality=80,format=auto/' +
         url.slice(marker.length);
}
/* @lm-test-export:end sizedThumb */

/**
 * The full-size URL for an order image, or null if it should not be zoomable.
 *
 * Only our own preview images qualify. An order with no cart mockup falls back
 * to a stock black-frame photo or the Shopify line-item image, and offering a
 * zoom there just hands the customer a bigger stock photo — which reads as
 * broken rather than as a feature.
 *
 * Strips any existing /cdn-cgi/image/ segment before re-sizing. The <img> on
 * screen carries the width=160 URL, and naively prefixing it would both nest
 * two resize segments (Cloudflare answers those with a 400) and upscale a
 * 160px file to 1200 instead of going back to the original.
 */
/* @lm-test-export:start zoomableFull */
function zoomableFull(url) {
  if (!url || typeof url !== 'string') return null;
  var origin = 'https://admin.skylitstudio.co.uk/';
  if (url.indexOf(origin) !== 0) return null;
  var path = url.slice(origin.length).replace(/^cdn-cgi\/image\/[^/]*\//, '');
  if (path.indexOf('api/preview-image/') !== 0) return null;
  return origin + 'cdn-cgi/image/width=1200,quality=85,format=auto/' + path;
}
/* @lm-test-export:end zoomableFull */

/**
 * Attributes that make an order image zoomable, or '' so it stays inert.
 *
 * tabindex + role are not decoration. An <img> is not focusable, so without
 * them the zoom is mouse-only: a keyboard user cannot reach it at all, and
 * returning focus to the opener on close is a silent no-op.
 */
function zoomAttr(url) {
  var full = zoomableFull(url);
  if (!full) return '';
  return ' data-full="' + full.replace(/"/g, '&quot;') + '"' +
         ' tabindex="0" role="button" aria-haspopup="dialog"';
}

/**
 * Enlarge an order image. Mirrors the tracking modal already in the drawer —
 * same fixed/inset/z-index shape — so the two behave identically rather than
 * inventing a second dismissal grammar for the same surface.
 *
 * Delegated on document so it survives the hub re-rendering its detail view,
 * which it does on every navigation.
 */
var hubLightbox = null;
var hubLightboxOpener = null;

function openHubLightbox(fullUrl, alt, opener) {
  if (!hubLightbox) {
    hubLightbox = document.createElement('div');
    hubLightbox.className = 'skylit-hub__zoom';
    hubLightbox.setAttribute('role', 'dialog');
    hubLightbox.setAttribute('aria-modal', 'true');
    hubLightbox.setAttribute('aria-label', 'Your design, enlarged');
    // The dim lives on this element, NOT on a child. An absolutely-positioned
    // child of a flex container picks up align-self: center and shrink-wraps,
    // so a <div class="backdrop" style="position:absolute; inset:0"> measured
    // 0x0 and the page was never dimmed at all.
    hubLightbox.innerHTML =
      '<button type="button" class="skylit-hub__zoom-close" aria-label="Close">&times;</button>' +
      '<img class="skylit-hub__zoom-img" alt="">';
    document.body.appendChild(hubLightbox);
    hubLightbox.addEventListener('click', function (e) {
      // The image itself is the one thing that does NOT dismiss: a customer
      // peering at their own print will tap it, and closing on that is
      // infuriating.
      if (e.target.closest('.skylit-hub__zoom-img')) return;
      closeHubLightbox();
    });
  }
  var img = hubLightbox.querySelector('.skylit-hub__zoom-img');
  img.src = fullUrl;
  img.alt = alt || 'Your design';
  hubLightboxOpener = opener || null;
  hubLightbox.classList.add('is-open');
  var close = hubLightbox.querySelector('.skylit-hub__zoom-close');
  if (close) close.focus();
}

function closeHubLightbox() {
  if (!hubLightbox) return;
  hubLightbox.classList.remove('is-open');
  // Drop the src so a large image is not held decoded behind the drawer.
  var img = hubLightbox.querySelector('.skylit-hub__zoom-img');
  if (img) img.removeAttribute('src');
  if (hubLightboxOpener && document.contains(hubLightboxOpener)) hubLightboxOpener.focus();
  hubLightboxOpener = null;
}

document.addEventListener('click', function (e) {
  var img = e.target.closest && e.target.closest('.skylit-hub__detail-product-img[data-full]');
  if (!img) return;
  e.preventDefault();
  openHubLightbox(img.getAttribute('data-full'), img.getAttribute('alt'), img);
});

document.addEventListener('keydown', function (e) {
  if (e.key === 'Escape' && hubLightbox && hubLightbox.classList.contains('is-open')) {
    closeHubLightbox();
    return;
  }
  // Enter / Space on a focused thumbnail, matching the role="button" we give it.
  if (e.key !== 'Enter' && e.key !== ' ') return;
  var img = e.target.closest && e.target.closest('.skylit-hub__detail-product-img[data-full]');
  if (!img) return;
  e.preventDefault();
  openHubLightbox(img.getAttribute('data-full'), img.getAttribute('alt'), img);
});

// Initialize - only once
(function() {
  if (window.skylitHub) return; // Prevent double initialization

  function init() {
    if (window.skylitHub) return;
    window.skylitHub = new SkylitCustomerHub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();